/**

 * @author Owais

 * @datetime July, 14th 2022

 * @description: This is Error Interceptor give a error from http request, retry 3 times after failer of call. 
 * still have an error print in console in dev env

 */

import { Injectable, NgZone } from '@angular/core';
import {
  HttpEvent,
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, Subject, of, timer } from 'rxjs';
import {
  retry,
  catchError,
  takeUntil,
  retryWhen,
  mergeMap,
  concatMap,
} from 'rxjs/operators';
import { checkGenericErrors } from 'src/app/shared/errors/generic-error-response';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { UserLoginService } from 'src/app/shared/services/user-login.service';
import { Router } from '@angular/router';
import { ERROR_CODES } from 'src/app/shared/constants/error-codes';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ErrorInterceptorInterceptor implements HttpInterceptor {
  private destroy$ = new Subject<void>();

  constructor(
    private zone: NgZone,
    private notificationService: NotificationService,
    private userService: UserLoginService,
    private router: Router,
    private translateService: TranslateService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      takeUntil(this.destroy$),
      retryWhen((errors) =>
        errors.pipe(
          concatMap((error, index) => {
            // Don't retry client errors (400-499), only retry server errors and network issues
            if (
              error instanceof HttpErrorResponse &&
              error.status >= 400 &&
              error.status < 500
            ) {
              return throwError(() => error);
            }
            // Retry up to 3 times with a 1 second delay
            if (index < 3) {
              return timer(1000);
            }
            return throwError(() => error);
          })
        )
      ),
      catchError((error: HttpErrorResponse) => {
        if (window.__dev__) console.log(error);

        if (error instanceof HttpErrorResponse) {
          if (error.status === Number(ERROR_CODES.ERROR_REQUEST_UNAUTHORIZED)) {
            // Clear all subscriptions
            this.destroy$.next();
            this.destroy$.complete();
            // Reset the subject for future requests
            this.destroy$ = new Subject<void>();
            this.userService.logoutLocally();
            return throwError(() => error);
          }
          checkGenericErrors(error.message, this.translateService);
        }

        this.zone.run(() => this.notificationService.showError(error.message));
        return throwError(() => error);
      }),
      catchError((error: any) => {
        this.zone.run(() => this.notificationService.showError(error.message));
        return throwError(() => error);
      })
    );
  }
}
